import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "grid grid-cols-3 sm:grid-cols-3 xl:grid-cols-3 gap-4 mb-4" }
const _hoisted_2 = { class: "text-sm text-gray-500 font-medium mb-2" }
const _hoisted_3 = { class: "text-xl font-medium truncate flex items-center" }
const _hoisted_4 = { class: "text-sm text-gray-500 font-medium mt-1" }
const _hoisted_5 = { class: "text-sm text-gray-500 font-medium mb-2" }
const _hoisted_6 = { class: "text-xl font-medium truncate flex items-center" }
const _hoisted_7 = { class: "text-sm text-gray-500 font-medium mt-1" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("div", { class: "text-sm text-gray-500 font-medium mb-2" }, " Volume ", -1)
const _hoisted_9 = { class: "text-xl font-medium truncate flex items-center" }
const _hoisted_10 = { class: "text-sm text-gray-500 font-medium mt-1" }

import { computed, watch } from 'vue';
import useNumbers from '@/composables/useNumbers';
import { differenceInMilliseconds, format } from 'date-fns';
import { FullPool } from '@/services/balancer/subgraph/types';
import numeral from 'numeral';
import useLge from '@/embr/lbp/composables/useLge';
import BalCard from '@/components/_global/BalCard/BalCard.vue';
import { GqlLge } from '@/embr/services/embr/embr-types';
import useWeb3 from '@/services/web3/useWeb3';
import { calculateLbpTokenPrice } from '@/embr/lbp/utils/lbpChartUtils';
import { getAddress } from '@ethersproject/address';

type Props = {
  lge: GqlLge;
  pool: FullPool;
};


export default _defineComponent({
  props: {
    lge: { type: null, required: true },
    pool: { type: null, required: true }
  } as unknown as undefined,
  setup(__props: {
  lge: GqlLge;
  pool: FullPool;
}) {

const props = __props


const { fNum } = useNumbers();
const { blockNumber } = useWeb3();

const {
  startsAt,
  endsAt,
  isBeforeStart,
  launchToken,
  collateralTokenPrice,
  refreshStartEndStatus,
  refetchQueriesOnBlockNumber,
  onNewTx
} = useLge(props.lge, props.pool);

const timeRemaining = computed(() =>
  isBeforeStart.value
    ? differenceInMilliseconds(startsAt.value, new Date())
    : differenceInMilliseconds(endsAt.value, new Date())
);

const countdownLabel = computed(() =>
  isBeforeStart.value ? 'Starts In' : 'Ends In'
);

const startDateFormatted = computed(() =>
  format(startsAt.value, 'MMM d, HH:mm')
);
const endDateFormatted = computed(() => format(endsAt.value, 'MMM d, HH:mm'));

const poolCollateralToken = computed(
  () =>
    props.pool.onchain.tokens[getAddress(props.lge.collateralTokenAddress)] ||
    null
);

const fundsRemoved = computed(() => {
  return (
    poolCollateralToken.value &&
    parseFloat(poolCollateralToken.value.balance) <= 0.0001
  );
});

const lbpData = computed(() => {
  const lge = props.lge;
  const poolLaunchToken =
    props.pool.onchain.tokens[getAddress(props.lge.tokenContractAddress)] ||
    null;
  const poolCollateralToken =
    props.pool.onchain.tokens[getAddress(props.lge.collateralTokenAddress)] ||
    null;

  if (!poolLaunchToken || !poolCollateralToken) {
    return null;
  }

  const remaining = parseFloat(poolLaunchToken.balance);
  const sold = parseFloat(lge.tokenAmount) - remaining;
  const tokenPrice = calculateLbpTokenPrice({
    tokenWeight: parseFloat(poolLaunchToken.weight),
    collateralBalance: parseFloat(poolCollateralToken.balance),
    collateralTokenPrice: collateralTokenPrice.value,
    collateralWeight: parseFloat(poolCollateralToken.weight),
    tokenBalance: parseFloat(poolLaunchToken.balance)
  });
  const predictedPrice = calculateLbpTokenPrice({
    tokenWeight: lge.tokenEndWeight,
    collateralWeight: lge.collateralEndWeight,
    collateralBalance: parseFloat(poolCollateralToken.balance),
    collateralTokenPrice: collateralTokenPrice.value,
    tokenBalance: parseFloat(poolLaunchToken.balance)
  });

  return {
    sold,
    remaining,
    percentSold: sold / parseFloat(lge.tokenAmount),
    tokenPrice: numeral(tokenPrice).format('$0,0.0000'),
    predictedPrice: numeral(predictedPrice).format('$0,0.0000'),
    embrWeight: numeral(parseFloat(poolLaunchToken.weight) * 100).format(
      '0.[00]'
    ),
    usdcWeight: numeral(parseFloat(poolCollateralToken.weight) * 100).format(
      '0.[00]'
    )
  };
});

function transformTime(slotProps) {
  return {
    ...slotProps,
    hours: slotProps.hours < 10 ? `0${slotProps.hours}` : slotProps.hours,
    minutes:
      slotProps.minutes < 10 ? `0${slotProps.minutes}` : slotProps.minutes,
    seconds:
      slotProps.seconds < 10 ? `0${slotProps.seconds}` : slotProps.seconds
  };
}

watch(blockNumber, () => {
  if (refetchQueriesOnBlockNumber.value < blockNumber.value) {
    onNewTx();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_vue_countdown = _resolveComponent("vue-countdown")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(BalCard, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(countdownLabel)), 1),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_vue_countdown, {
            time: _unref(timeRemaining),
            transform: transformTime,
            onEnd: _cache[0] || (_cache[0] = ($event: any) => (_unref(refreshStartEndStatus)()))
          }, {
            default: _withCtx(({ days, hours, minutes, seconds }) => [
              _createTextVNode(_toDisplayString(days > 1
              ? `${days} days`
              : days === 1
              ? `0${days}:${hours}:${minutes}:${seconds}`
              : `${hours}:${minutes}:${seconds}`), 1)
            ]),
            _: 1
          }, 8, ["time"])
        ]),
        _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(startDateFormatted)) + " - " + _toDisplayString(_unref(endDateFormatted)), 1)
      ]),
      _: 1
    }),
    _createVNode(BalCard, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_5, " Current " + _toDisplayString(_unref(launchToken)?.symbol) + " Price ", 1),
        _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(fundsRemoved) ? '-' : _unref(lbpData) ? _unref(lbpData).tokenPrice : ''), 1),
        _createElementVNode("div", _hoisted_7, " Predicted price*: " + _toDisplayString(_unref(fundsRemoved) ? '-' : _unref(lbpData) ? _unref(lbpData).predictedPrice : ''), 1)
      ]),
      _: 1
    }),
    _createVNode(BalCard, null, {
      default: _withCtx(() => [
        _hoisted_8,
        _createElementVNode("div", _hoisted_9, _toDisplayString(_unref(fNum)(props.pool.totalSwapVolume, 'usd_m')), 1),
        _createElementVNode("div", _hoisted_10, _toDisplayString(_unref(lbpData) ? _unref(fNum)(_unref(lbpData).sold, 'token_lg') : '') + " of " + _toDisplayString(_unref(fNum)(__props.lge.tokenAmount, 'token_lg')) + " sold ", 1)
      ]),
      _: 1
    })
  ]))
}
}

})